import { uiText, useUiLanguage } from '../../../utils/uiTranslation';
import React, { useEffect, useState } from 'react';
import { ANALYTICS_GRAPH_TYPES } from './graphCatalog';

const formatDays = (value, unit = 'days') => `${Number(value || 0).toFixed(1)} ${unit}`;

const DistributionVisual = ({ model }) => {
  useUiLanguage();
  const rootDataset = model.dataset;
  const [selectedViewId, setSelectedViewId] = useState(rootDataset?.selectedViewId || rootDataset?.selectors?.[0]?.id || '');
  useEffect(() => {
    const nextDefault = rootDataset?.selectedViewId || rootDataset?.selectors?.[0]?.id || '';
    if (!nextDefault) return;
    setSelectedViewId((currentId) => {
      if (!rootDataset?.views?.[currentId] || currentId !== nextDefault) {
        return nextDefault;
      }
      return currentId;
    });
  }, [rootDataset]);
  const dataset = rootDataset?.views?.[selectedViewId] || rootDataset;
  const sizeClass = model.size || 'medium';
  const points = dataset?.points || [];
  if (!points.length) return null;

  if (sizeClass === 'compact') {
    const rankedPoints = [...points].sort((left, right) => (right.y || 0) - (left.y || 0));
    return (
      <div className="analytics-visual analytics-visual-scatter-list">
        {rankedPoints.map((point) => (
          <div key={point.id} className="analytics-visual-scatter-list-row">
            <div className="analytics-visual-scatter-list-name">
              <span className="analytics-visual-legend-swatch accent" />
              <span className="analytics-visual-label">{uiText(point.label)}</span>
            </div>
            <div className="analytics-visual-scatter-list-metrics">
              <strong>{point.y.toFixed(1)}d</strong>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (model.activeGraph === ANALYTICS_GRAPH_TYPES.HISTOGRAM) {
    const max = Math.max(...points.map((point) => point.y), 1);
    return (
      <div className={`analytics-visual analytics-visual-time-bars ${sizeClass}`}>
        {rootDataset?.selectors?.length ? (
          <div className="analytics-visual-source-picker" role="tablist" aria-label={rootDataset.selectorLabel || uiText("Selector")}>
            {rootDataset.selectors.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`analytics-visual-source-pill${selectedViewId === option.id ? ' active' : ''}`}
                onClick={() => setSelectedViewId(option.id)}
              >
                {uiText(option.label)}
              </button>
            ))}
          </div>
        ) : null}
        <div className="analytics-visual-time-grid">
          {points.map((point) => (
            <div key={point.id} className="analytics-visual-time-bar-col">
              <div className="analytics-visual-time-bar-wrap">
                <div className="analytics-visual-time-bar accent" style={{ height: `${Math.max(16, (point.y / max) * 132)}px` }} />
              </div>
              <strong className="analytics-visual-sub">{point.y.toFixed(1)}</strong>
              <span className="analytics-visual-axis-label">{uiText(point.label)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (model.activeGraph === ANALYTICS_GRAPH_TYPES.SCATTER) {
    const maxX = Math.max(...points.map((point) => point.x), 1);
    const maxY = Math.max(...points.map((point) => point.y), 1);
    return (
      <div className={`analytics-visual analytics-visual-scatter ${sizeClass}`}>
        {rootDataset?.selectors?.length ? (
          <div className="analytics-visual-source-picker" role="tablist" aria-label={rootDataset.selectorLabel || uiText("Selector")}>
            {rootDataset.selectors.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`analytics-visual-source-pill${selectedViewId === option.id ? ' active' : ''}`}
                onClick={() => setSelectedViewId(option.id)}
              >
                {uiText(option.label)}
              </button>
            ))}
          </div>
        ) : null}
        <svg viewBox="0 0 360 170" className="analytics-visual-trend-svg" role="img" aria-label={uiText("Distribution scatter")}>
          <line x1="20" y1="150" x2="340" y2="150" className="analytics-visual-axis" />
          <line x1="20" y1="20" x2="20" y2="150" className="analytics-visual-axis" />
          {points.map((point) => {
            const x = 24 + (point.x / maxX) * 300;
            const y = 146 - (point.y / maxY) * 112;
            return (
              <g key={point.id}>
                <circle cx={x} cy={y} r={Math.max(6, point.size || 8)} className="analytics-visual-dot accent" fillOpacity="0.4" />
                <text x={x} y={Math.max(16, y - 12)} textAnchor="middle" className="analytics-visual-axis-label">
                  {uiText(point.label)}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    );
  }

  const unit = dataset?.unit || 'days';
  const scaleMin = Math.min(...points.map((point) => Number(point.min ?? point.y ?? 0)), 0);
  const scaleMax = Math.max(...points.map((point) => Number(point.max ?? point.y ?? 0)), 1);
  const teamAverage = Number(dataset?.summary?.teamAverage || 0);
  const toPercent = (value) => {
    const numericValue = Number(value) || 0;
    if (scaleMax <= scaleMin) return 50;
    return ((numericValue - scaleMin) / (scaleMax - scaleMin)) * 100;
  };

  return (
    <div className={`analytics-visual analytics-visual-distribution-rows ${sizeClass}`}>
      {rootDataset?.selectors?.length ? (
        <div className="analytics-visual-source-picker" role="tablist" aria-label={rootDataset.selectorLabel || uiText("Selector")}>
          {rootDataset.selectors.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`analytics-visual-source-pill${selectedViewId === option.id ? ' active' : ''}`}
              onClick={() => setSelectedViewId(option.id)}
            >
              {uiText(option.label)}
            </button>
          ))}
        </div>
        ) : null}
      {points.map((point) => {
        const minLeft = toPercent(point.min);
        const q1Left = toPercent(point.q1 ?? point.min);
        const medianLeft = toPercent(point.median ?? point.y);
        const q3Left = toPercent(point.q3 ?? point.max);
        const maxLeft = toPercent(point.max);
        const averageLeft = toPercent(point.y);
        const teamAverageLeft = toPercent(teamAverage);

        return (
          <div key={point.id} className="analytics-visual-distribution-row">
            <div className="analytics-visual-distribution-header">
              <span className="analytics-visual-label">{uiText(point.label)}</span>
              <div className="analytics-visual-distribution-metrics">
                <span className="analytics-visual-sub">{uiText("{{count}} hires", { count: point.x })}</span>
                <strong>{formatDays(point.median, unit)}</strong>
              </div>
            </div>
            <div className="analytics-visual-distribution-track">
              <span className="analytics-visual-distribution-benchmark" style={{ left: `${teamAverageLeft}%` }} />
              <span className="analytics-visual-distribution-whisker" style={{ left: `${minLeft}%`, width: `${Math.max(2, maxLeft - minLeft)}%` }} />
              <span className="analytics-visual-distribution-cap start" style={{ left: `${minLeft}%` }} />
              <span className="analytics-visual-distribution-cap end" style={{ left: `${maxLeft}%` }} />
              <span className="analytics-visual-distribution-box" style={{ left: `${q1Left}%`, width: `${Math.max(4, q3Left - q1Left)}%` }} />
              <span className="analytics-visual-distribution-median" style={{ left: `${medianLeft}%` }} />
              <span className="analytics-visual-distribution-average" style={{ left: `${averageLeft}%` }} />
              <span className="analytics-visual-distribution-end-label start" style={{ left: `${minLeft}%` }}>
                {formatDays(point.min, unit)}
              </span>
              <span className="analytics-visual-distribution-end-label end" style={{ left: `${maxLeft}%` }}>
                {formatDays(point.max, unit)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default DistributionVisual;
